import { useEffect, useState } from "react";
import { useApp } from "../vault/ctx";
import { Pills } from "../vault/Common";
import {
  getInsightsSummary,
  getHottestDesigns,
  getClientEngagement,
  type InsightsSummary,
  type HotDesign,
  type ClientEngagement,
} from "../../features/insights/insightsApi";

const SIGNAL: Record<ClientEngagement["signal"], [string, string]> = {
  hot: ["green", "Hot"],
  warm: ["gold", "Warm"],
  cold: ["gray", "Going cold"],
};

const ZERO: InsightsSummary = {
  linkViews: 0, activeClients: 0, avgTimeToApprovalDays: 0, firstPassApprovalPct: 0,
  linkViewsDelta: 0, activeClientsDelta: 0, avgTimeToApprovalDaysDelta: 0, firstPassApprovalPctDelta: 0,
};

/** ISO/Date string → "1 h ago"; falls back to the raw string if unparseable. */
function relSeen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)} min ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** Delta chip: arrow follows the sign, up/dn class follows good/bad. All backend
 *  deltas are % change; `lowerBetter` flips good/bad for "time to approval". */
function Delta({ v, lowerBetter = false }: { v: number; lowerBetter?: boolean }) {
  if (!v) return null; // 0 == no change (or empty workspace) → show nothing
  const good = lowerBetter ? v < 0 : v > 0;
  return <span className={good ? "up" : "dn"}>{v > 0 ? "▲" : "▼"} {Math.abs(v)}%</span>;
}

export default function InsightsPage() {
  const { go } = useApp();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<InsightsSummary | null>(null);
  const [designs, setDesigns] = useState<HotDesign[] | null>(null);
  const [clients, setClients] = useState<ClientEngagement[] | null>(null);

  useEffect(() => {
    let alive = true;
    getInsightsSummary(days).then((r) => alive && setSummary(r)).catch(() => alive && setSummary(ZERO));
    getHottestDesigns(days).then((r) => alive && setDesigns(r)).catch(() => alive && setDesigns([]));
    getClientEngagement(days).then((r) => alive && setClients(r)).catch(() => alive && setClients([]));
    return () => { alive = false; };
  }, [days]);

  const maxViews = Math.max(1, ...(designs ?? []).map((d) => d.views));

  return (
    <>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="page-title">Insights</div>
          <div className="page-sub">What your clients actually look at — so you follow up on the right designs at the right moment.</div>
        </div>
        <Pills options={["7 d", "30 d", "90 d"]} value={`${days} d`} onChange={(v) => setDays(parseInt(v as string, 10))} />
      </div>
      <div className="stat-row">
        <div className="stat"><div className="sv">{summary ? summary.linkViews.toLocaleString() : "—"}</div><div className="sl">Link views · {days} d {summary && <Delta v={summary.linkViewsDelta} />}</div></div>
        <div className="stat"><div className="sv">{summary ? summary.activeClients : "—"}</div><div className="sl">Active clients {summary && <Delta v={summary.activeClientsDelta} />}</div></div>
        <div className="stat"><div className="sv">{summary ? `${summary.avgTimeToApprovalDays} days` : "—"}</div><div className="sl">Avg. time to approval {summary && <Delta v={summary.avgTimeToApprovalDaysDelta} lowerBetter />}</div></div>
        <div className="stat"><div className="sv">{summary ? `${summary.firstPassApprovalPct}%` : "—"}</div><div className="sl">Designs approved first pass {summary && <Delta v={summary.firstPassApprovalPctDelta} />}</div></div>
      </div>
      <div className="two-col" style={{ marginTop: 14 }}>
        <div className="card">
          <h3>Hottest designs · {days} days</h3>
          <div className="hint" style={{ marginBottom: 6 }}>By client views. HOT = viewed 3+ times by the same client — they're deciding.</div>
          {designs === null ? (
            <div className="hint">Loading…</div>
          ) : designs.length === 0 ? (
            <div className="hint">No design views yet.</div>
          ) : designs.map((d) => (
            <div key={d.fileId} className="hbar"><span className="hb-name">{d.name}{d.hot ? <span className="badge gold" style={{ marginLeft: 6, fontSize: 8.5 }}>HOT</span> : null}</span><div className="hb-track"><div className="hb-fill" style={{ width: (d.views / maxViews) * 100 + "%" }}></div></div><span className="hb-v">{d.views}</span></div>
          ))}
          <div className="anno">ⓘ Tip: “Aurelia viewed the Solitaire 6× this week but hasn't approved — send a nudge?” <a href="#" onClick={e => { e.preventDefault(); go("approvals"); }} style={{ color: "var(--blue)" }}>Go to Approvals</a></div>
        </div>
        <div className="card">
          <h3>Client engagement</h3>
          <table className="list-table on" style={{ border: "none", marginTop: 8 }}>
            <thead><tr><th>Client</th><th>Views</th><th>Downloads</th><th>Last seen</th><th>Signal</th></tr></thead>
            <tbody>
              {clients === null ? (
                <tr><td className="nm" colSpan={5}>Loading…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td className="nm" colSpan={5}>No client activity yet.</td></tr>
              ) : clients.map((c) => {
                const [bc, bt] = SIGNAL[c.signal];
                return (
                  <tr key={c.clientName}><td className="nm">{c.clientName}</td><td>{c.views}</td><td>{c.downloads}</td><td>{relSeen(c.lastSeen)}</td><td><span className={"badge " + bc}>{bt}</span></td></tr>
                );
              })}
            </tbody>
          </table>
          <div className="anno">ⓘ “Going cold” clients get a suggested re-engagement lookbook, one click to send.</div>
        </div>
      </div>
    </>
  );
}
